import {
  base_colorAlert,
  base_colorAlertHover,
  base_colorContentBorder,
  base_contentHover,
  tag_colorTagNewBackground,
  tag_colorTagNewBackgroundHover,
  tag_colorTagNewText,
  tag_colorTagNewTextHover,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import {
  cornerRadius,
  flyout_outerBlockVSpacing,
  spacing,
} from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_sm,
  fontRegular,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

export default css`
  display: flex;
  flex-direction: column;
  gap: ${flyout_outerBlockVSpacing};
  position: relative;

  .details {
    display: flex;
    flex-direction: row;
    gap: ${spacing(2)};
    align-items: center;
    ${fontRegular}
    color: ${text_colorTextPrimary};

    .transaction-card-name {
      display: block;
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    & > *:not(:first-child) {
      display: flex;
      gap: ${spacing(2)};
      align-items: center;

      ::before {
        content: '•';
        color: ${text_colorTextSecondary};
        ${fontRegular};
      }
    }
  }

  .decline-reason {
    color: ${base_colorAlert};
    display: block;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .timeline {
    display: flex;
    flex-direction: column;
    gap: ${spacing(3)};
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: 18px; // Center of 36px diameter icons
      width: 1px;
      // Expecting that this timeline list will eventually have
      // a variable number of timeline-events in it, so making
      // the height 100% of the parent .timeline container with
      // slight adjustment to account for the icon not being the
      // full height of the timeline-event row, and the row's v padding
      height: calc(100% - 6px);
      background-color: ${base_colorContentBorder};
    }

    .timeline-event {
      display: flex;
      align-items: flex-start;
      gap: ${spacing(3)};
      position: relative;
    }
  }

  .feedback-button {
    &:hover g {
      fill: transparent;
    }
    &.negative {
      color: ${base_colorAlert};

      &:hover {
        color: ${base_colorAlertHover};
      }
    }
  }

  .expense-policy-ai-banner {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: ${spacing(3)};
    padding: ${spacing(4)} ${spacing(5)};
    border: 1px solid ${base_colorContentBorder};
    border-radius: ${cornerRadius};
    background-color: ${tag_colorTagNewBackground};
  }

  .policy-insight-icon {
    display: flex;
    flex-shrink: 0;
  }

  .policy-insight-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: ${spacing(2)};
    min-width: 0;
  }

  .policy-insight-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${spacing(4)};
    min-height: ${spacing(8)};
  }

  .policy-insight-heading {
    display: flex;
    align-items: center;
    gap: ${spacing(1)};
    min-width: 0;
  }

  .policy-insight-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${spacing(2)};
    color: ${text_colorTextPrimary};

    /* Span the full card width. The content is a stretched flex child, so
       pulling it left past the collapse toggle (spacing(8)), the tightened
       collapse->icon gap (spacing(1)), the 24px sparkle icon (spacing(6)) and
       the icon->body gap (spacing(3)) lands its left edge at the card's
       inner-left and keeps its right edge for an even full-width block. */
    margin-left: calc(
      -1 * (${spacing(8)} + ${spacing(1)} + ${spacing(6)} + ${spacing(3)})
    );
  }

  .policy-insight-header-actions {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: ${spacing(1)};
  }

  .policy-insight-beta-tag {
    padding-left: ${spacing(2)};
  }

  .policy-insight-collapse-toggle.no-style {
    width: ${spacing(8)};
    min-width: ${spacing(8)};
    height: ${spacing(8)};
    min-height: ${spacing(8)};
    padding: ${spacing(1)};
    border-radius: ${cornerRadius};
    /* tighten the gap to the sparkle icon down to the See more details gap
       (spacing(1)) rather than the banner's spacing(3) */
    margin-right: calc(${spacing(1)} - ${spacing(3)});

    &:hover {
      background-color: ${base_contentHover};
    }

    svg {
      width: ${spacing(5)};
      height: ${spacing(5)};
    }
  }

  .policy-insight-key-takeaways {
    display: flex;
    flex-direction: column;
    /* content uses align-items: flex-start, so stretch this box to span the
       full width regardless of how short the takeaways are */
    align-self: stretch;
    gap: ${spacing(2)};
    padding: ${spacing(3)} ${spacing(4)};
    border: 1px solid ${tag_colorTagNewBackgroundHover};
    border-radius: ${cornerRadius};
    background-color: rgba(255, 255, 255, 0.55);
  }

  .policy-insight-key-takeaways-label {
    ${font_sm};
    font-family: ${font_family_weight_medium};
    color: ${tag_colorTagNewText};
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .policy-insight-footer {
    display: flex;
    /* let the toggle + link stack instead of overflowing at narrow widths
       (WCAG 1.4.10 reflow at 320px) */
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    align-self: stretch;
    gap: ${spacing(4)};
  }

  .policy-insight-details-link {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${spacing(1)};
    /* keep the link within the card and let its label wrap when the footer
       reflows onto its own line */
    max-width: 100%;
  }

  .policy-insight-details-toggle.no-style {
    display: inline-flex;
    align-items: center;
    gap: ${spacing(1)};
    padding: 0;
    color: ${text_colorTextPrimary};

    svg {
      width: ${spacing(5)};
      height: ${spacing(5)};
    }
  }

  /* Give the chevron the same button box + tap target as the header collapse
     toggle (a spacing(8) square with the spacing(5) chevron centered), so the
     two chevrons align and share the same spacing to the content beside them. */
  .policy-insight-details-toggle-chevron {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: ${spacing(8)};
    height: ${spacing(8)};
    border-radius: ${cornerRadius};
  }

  .policy-insight-details-toggle.no-style:hover
    .policy-insight-details-toggle-chevron {
    background-color: ${base_contentHover};
  }

  .policy-insight-text {
    display: flex;
    flex-direction: column;
    align-self: stretch;
    gap: ${spacing(1)};
  }

  .policy-insight-text-line {
    display: block;
  }

  .policy-insight-text-bullets {
    margin: ${spacing(1)} 0;
    padding-left: ${spacing(4)};
    padding-right: ${spacing(2)};
  }

  .policy-insight-text-bullet {
    display: list-item;
    margin-bottom: ${spacing(2)};

    &:last-child {
      margin-bottom: 0;
    }
  }

  .policy-feedback-actions {
    display: flex;
    flex-shrink: 0;
    flex-direction: row;
    align-items: center;
    gap: ${spacing(1)};
  }

  .policy-feedback-button.no-border {
    width: ${spacing(8)};
    min-width: ${spacing(8)};
    height: ${spacing(8)};
    min-height: ${spacing(8)};
    padding: ${spacing(2)};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${spacing(1)};
    color: ${tag_colorTagNewText};
    background-color: transparent;
    border: 1px solid transparent;
    border-radius: ${cornerRadius};
    box-shadow: none;

    svg {
      width: ${spacing(4)};
      height: ${spacing(4)};
      flex-shrink: 0;
    }

    &:hover {
      background-color: ${base_contentHover};
      border-color: ${base_colorContentBorder};
      box-shadow: none;
      color: ${tag_colorTagNewTextHover};
    }

    &.selected {
      background-color: ${base_contentHover};
      border-color: ${base_colorContentBorder};
      box-shadow: none;
      color: ${tag_colorTagNewTextHover};
    }
  }
`

export const iconStyle = css`
  width: ${spacing(5)};
  height: ${spacing(5)};
`
